import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import {
  useCheckEmailLocalPart,
  useClinicEmailTemplates,
  useResetClinicEmailTemplate,
  useSetClinicEmailLocalPart,
  useSetClinicEmailSignature,
  useUpsertClinicEmailTemplate,
  type LocalPartAvailability,
} from '@/hooks/useClinicEmail';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Banner } from '@/components/ui/Banner';
import { Select } from '@/components/ui/Select';
import { communicationInboxPath } from '@/lib/communicationInbox';
import {
  SaveBar,
  SectionCard,
  SettingsLoading,
  SettingsSubpageHeader,
} from './settingsChrome';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  CLINIC_EMAIL_DOMAIN,
  OVERRIDABLE_TEMPLATE_KEYS,
  SIGNATURE_TEXT_MAX,
  TEMPLATE_BODY_MAX,
  TEMPLATE_SUBJECT_MAX,
  clinicFromPreview,
  containsCpr,
  defaultTemplateSource,
  localPartIssue,
  mergeFieldsForTemplate,
  renderTemplateSource,
  sampleMergeFields,
  type EmailLocale,
  type OverridableTemplateKey,
} from '@/lib/clinicEmail/catalog';
import { clinicEmailSaveErrorToast } from '@/lib/clinicEmail/saveError';
import {
  mergeAppointmentEmailReminderSettings,
  readAppointmentEmailReminderSettings,
} from '@/lib/clinicEmail/appointmentReminders';

const LOCAL_PART_DEBOUNCE_MS = 400;

export function ClinicEmail() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin, hasPermission } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const { data: templateRows = [] } = useClinicEmailTemplates(activeClinicId);
  const checkLocalPart = useCheckEmailLocalPart(activeClinicId);
  const setLocalPart = useSetClinicEmailLocalPart(activeClinicId);
  const setSignature = useSetClinicEmailSignature(activeClinicId);
  const updateClinic = useUpdateClinicSettings(activeClinicId);
  const upsertTemplate = useUpsertClinicEmailTemplate(activeClinicId);
  const resetTemplate = useResetClinicEmailTemplate(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));
  const canOpenInbox = Boolean(
    activeClinicId &&
      (isPlatformAdmin ||
        isClinicAdmin(activeClinicId) ||
        hasPermission(activeClinicId, 'communication', 'view')),
  );

  const [localPart, setLocalPartValue] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [reminderLeadHours, setReminderLeadHours] = useState(24);
  const [availability, setAvailability] = useState<LocalPartAvailability | null>(null);
  const [activeKey, setActiveKey] = useState<OverridableTemplateKey>('appointment_confirmation');
  const [locale, setLocale] = useState<EmailLocale>('da');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!clinic) return;
    setLocalPartValue(clinic.email_local_part ?? '');
    setSignatureText(clinic.email_signature_text ?? '');
    const reminders = readAppointmentEmailReminderSettings(clinic.email_settings);
    setRemindersEnabled(reminders.enabled);
    setReminderLeadHours(reminders.leadHours);
  }, [clinic]);

  const override = useMemo(
    () =>
      templateRows.find((row) => row.template_key === activeKey && row.locale === locale) ?? null,
    [templateRows, activeKey, locale],
  );

  useEffect(() => {
    const source = override
      ? { subject: override.subject, body: override.body_html }
      : defaultTemplateSource(activeKey, locale);
    setSubject(source.subject);
    setBody(source.body);
  }, [activeKey, locale, override]);

  useEffect(() => {
    if (!canEdit || !clinic) {
      setAvailability(null);
      return;
    }
    const saved = (clinic.email_local_part ?? '').toLowerCase();
    const issue = localPartIssue(localPart);
    if (issue === 'empty' || localPart.trim().toLowerCase() === saved) {
      setAvailability(null);
      return;
    }
    if (issue) {
      setAvailability({
        available: false,
        reason: issue === 'reserved' ? 'reserved' : 'invalid',
        normalized: null,
      });
      return;
    }
    const handle = window.setTimeout(() => {
      void checkLocalPart
        .mutateAsync(localPart)
        .then(setAvailability)
        .catch(() => setAvailability(null));
    }, LOCAL_PART_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [canEdit, clinic, localPart, checkLocalPart]);

  const clinicName = clinic?.name ?? '';
  const previewAddress = clinicFromPreview(clinicName, localPart);
  const mergeFields = mergeFieldsForTemplate(activeKey);
  const samplePreview = useMemo(
    () =>
      renderTemplateSource(
        { subject, body },
        locale,
        sampleMergeFields(activeKey, locale),
        clinicName || (locale === 'da' ? 'Eksempel Klinik' : 'Example Clinic'),
        { text: signatureText },
      ),
    [subject, body, locale, activeKey, clinicName, signatureText],
  );

  const localPartError = (() => {
    if (!localPart.trim()) return undefined;
    const issue = localPartIssue(localPart);
    if (issue === 'reserved') return t('settings.email.local_part_reserved');
    if (issue === 'invalid' || issue === 'too_long') return t('settings.email.local_part_invalid');
    if (availability && !availability.available) {
      if (availability.reason === 'taken') return t('settings.email.local_part_taken');
      if (availability.reason === 'reserved') return t('settings.email.local_part_reserved');
      if (availability.reason === 'invalid') return t('settings.email.local_part_invalid');
    }
    return undefined;
  })();

  const savingClinic =
    setLocalPart.isPending || setSignature.isPending || updateClinic.isPending;

  const onSubmitClinic = async (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit || !clinic) return;
    if (containsCpr(signatureText)) {
      toast.error(t('settings.email.signature_cpr_blocked'));
      return;
    }
    if (signatureText.length > SIGNATURE_TEXT_MAX) {
      toast.error(t('settings.email.signature_too_long'));
      return;
    }
    const trimmed = localPart.trim();
    const savedPart = clinic.email_local_part ?? '';
    const issue = trimmed ? localPartIssue(trimmed) : null;
    switch (issue) {
      case 'reserved':
        toast.error(t('settings.email.local_part_reserved'));
        return;
      case 'invalid':
      case 'too_long':
        toast.error(t('settings.email.local_part_invalid'));
        return;
      case 'empty':
      case null:
        break;
      default: {
        const _exhaustive: never = issue;
        return _exhaustive;
      }
    }
    if (trimmed && availability && !availability.available) {
      switch (availability.reason) {
        case 'reserved':
          toast.error(t('settings.email.local_part_reserved'));
          return;
        case 'taken':
          toast.error(t('settings.email.local_part_taken'));
          return;
        case 'invalid':
          toast.error(t('settings.email.local_part_invalid'));
          return;
        case null:
          toast.error(t('settings.email.local_part_unavailable'));
          return;
        default: {
          const _exhaustive: never = availability.reason;
          return _exhaustive;
        }
      }
    }
    try {
      if (trimmed && trimmed.toLowerCase() !== savedPart.toLowerCase()) {
        await setLocalPart.mutateAsync(trimmed);
      }
      if (signatureText !== (clinic.email_signature_text ?? '')) {
        await setSignature.mutateAsync(signatureText);
      }
      const savedReminders = readAppointmentEmailReminderSettings(clinic.email_settings);
      const nextLead = Math.min(168, Math.max(1, Math.round(reminderLeadHours) || 24));
      if (
        savedReminders.enabled !== remindersEnabled ||
        savedReminders.leadHours !== nextLead
      ) {
        await updateClinic.mutateAsync({
          email_settings: mergeAppointmentEmailReminderSettings(clinic.email_settings, {
            enabled: remindersEnabled,
            leadHours: nextLead,
          }),
        });
      }
      toast.success(t('settings.email.saved'));
    } catch (err) {
      const mapped = clinicEmailSaveErrorToast(err);
      toast.error(mapped.reason ? t(mapped.key, { reason: mapped.reason }) : t(mapped.key));
    }
  };

  const insertMergeField = (field: string) => {
    setBody((current) => `${current}{{${field}}}`);
  };

  const onSaveTemplate = async (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    if (containsCpr(subject) || containsCpr(body)) {
      toast.error(t('settings.email.template_cpr_blocked'));
      return;
    }
    try {
      await upsertTemplate.mutateAsync({
        templateKey: activeKey,
        locale,
        subject,
        body,
      });
      toast.success(t('settings.email.template_saved'));
    } catch (err) {
      const mapped = clinicEmailSaveErrorToast(err);
      toast.error(mapped.reason ? t(mapped.key, { reason: mapped.reason }) : t(mapped.key));
    }
  };

  const onResetTemplate = async () => {
    if (!canEdit) return;
    try {
      await resetTemplate.mutateAsync({ templateKey: activeKey, locale });
      const source = defaultTemplateSource(activeKey, locale);
      setSubject(source.subject);
      setBody(source.body);
      toast.success(t('settings.email.template_reset'));
    } catch (err) {
      const mapped = clinicEmailSaveErrorToast(err);
      toast.error(mapped.reason ? t(mapped.key, { reason: mapped.reason }) : t(mapped.key));
    }
  };

  if (isLoading) return <SettingsLoading />;
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <SettingsSubpageHeader
        icon={<Mail className="h-5 w-5" aria-hidden />}
        title={t('settings.nav.email', 'E-mail')}
        subtitle={t('settings.email.subtitle')}
      />
      <Banner tone="info">{t('settings.email.setup_note')}</Banner>
      {canOpenInbox ? (
        <SectionCard
          title={t('settings.email.inbox_title', 'Uidentificeret e-mail')}
          subtitle={t(
            'settings.email.inbox_moved',
            'Uidentificerede e-mails ligger nu under Kommunikation → E-mail.',
          )}
        >
          <ButtonLink to={communicationInboxPath('email')} variant="secondary" size="sm">
            {t('settings.email.inbox_open', 'Åbn E-mail-indbakke')}
          </ButtonLink>
        </SectionCard>
      ) : null}

      <form onSubmit={onSubmitClinic} className="flex min-w-0 flex-col gap-s4">
        <SectionCard
          title={t('settings.email.address_title')}
          subtitle={t('settings.email.address_desc')}
          readOnly={!canEdit}
        >
          <div className="flex min-w-0 flex-col gap-s4">
            <div>
              <label htmlFor="clinic-email-local-part" className={cn(TYPE.label, 'text-ds-text')}>
                {t('settings.email.local_part')}
              </label>
              <div className="mt-s2 flex min-w-0 items-stretch">
                <Input
                  id="clinic-email-local-part"
                  name="clinic-email-local-part"
                  value={localPart}
                  onChange={(e) => setLocalPartValue(e.target.value)}
                  disabled={!canEdit}
                  autoComplete="off"
                  spellCheck={false}
                  className="rounded-r-none"
                />
                <span className="inline-flex items-center rounded-r-xl border border-l-0 border-ds-field bg-ds-sunk px-s3 text-ds-muted">
                  @{CLINIC_EMAIL_DOMAIN}
                </span>
              </div>
              {localPartError ? (
                <p className={cn(TYPE.small, 'mt-s1 text-ds-red-ink')}>{localPartError}</p>
              ) : null}
              {!localPartError && availability?.available ? (
                <p className={cn(TYPE.small, 'mt-s1 text-ds-green-ink')}>
                  {t('settings.email.local_part_available')}
                </p>
              ) : null}
            </div>
            <p className={cn(TYPE.body, 'text-ds-muted')}>
              {t('settings.email.address_preview_label')}:{' '}
              <span className="text-ds-text">{previewAddress}</span>
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title={t('settings.email.signature_title')}
          subtitle={t('settings.email.signature_desc')}
          readOnly={!canEdit}
        >
          <Textarea
            name="clinic-email-signature"
            label={t('settings.email.signature_label')}
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
            disabled={!canEdit}
            rows={6}
            maxLength={SIGNATURE_TEXT_MAX}
            hint={t('settings.email.signature_hint')}
          />
        </SectionCard>

        <SectionCard
          title={t('settings.email.reminders_title')}
          subtitle={t('settings.email.reminders_desc')}
          readOnly={!canEdit}
        >
          <div className="flex min-w-0 flex-col gap-s4">
            <Checkbox
              id="clinic-email-reminders"
              checked={remindersEnabled}
              onChange={(e) => setRemindersEnabled(e.target.checked)}
              disabled={!canEdit}
              label={t('settings.email.reminders_enabled')}
              hint={t('settings.email.reminders_enabled_hint')}
            />
            <Input
              id="clinic-email-reminder-lead"
              type="number"
              min={1}
              max={168}
              label={t('settings.email.reminder_lead_hours')}
              hint={t('settings.email.reminder_lead_hours_hint')}
              value={String(reminderLeadHours)}
              onChange={(e) => setReminderLeadHours(Number(e.target.value))}
              disabled={!canEdit || !remindersEnabled}
            />
          </div>
        </SectionCard>

        <SaveBar canEdit={canEdit} pending={savingClinic} />
      </form>

      <form onSubmit={onSaveTemplate} className="flex min-w-0 flex-col gap-s4">
        <SectionCard
          title={t('settings.email.templates_title')}
          subtitle={t('settings.email.templates_desc')}
          readOnly={!canEdit}
          action={
            <span className={cn(TYPE.small, override ? 'text-ds-teal' : 'text-ds-muted')}>
              {override ? t('settings.email.template_custom') : t('settings.email.template_default')}
            </span>
          }
        >
          <div className="flex min-w-0 flex-col gap-s4">
            <Select
              id="email-template-kind"
              label={t('settings.email.template_kind')}
              value={activeKey}
              onChange={(e) => setActiveKey(e.target.value as OverridableTemplateKey)}
            >
              {OVERRIDABLE_TEMPLATE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`settings.email.kinds.${key}`)}
                </option>
              ))}
            </Select>
            <div>
              <p className={cn(TYPE.label, 'text-ds-text')}>{t('settings.email.template_locale')}</p>
              <div className="mt-s2 flex flex-wrap gap-s2">
                <Button
                  type="button"
                  size="sm"
                  variant={locale === 'da' ? 'primary' : 'secondary'}
                  onClick={() => setLocale('da')}
                >
                  {t('settings.email.locale_da')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={locale === 'en' ? 'primary' : 'secondary'}
                  onClick={() => setLocale('en')}
                >
                  {t('settings.email.locale_en')}
                </Button>
              </div>
            </div>
            <Input
              name="email-template-subject"
              label={t('settings.email.template_subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!canEdit}
              autoComplete="off"
              maxLength={TEMPLATE_SUBJECT_MAX}
            />
            <div>
              <p className={cn(TYPE.label, 'mb-s2 text-ds-text')}>
                {t('settings.email.merge_fields')}
              </p>
              <div className="flex flex-wrap gap-s2">
                {mergeFields.map((field) => (
                  <button
                    key={field}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => insertMergeField(field)}
                    className="rounded-full bg-ds-teal-tint px-s3 py-s1 text-ds-teal disabled:opacity-60"
                  >
                    {`{{${field}}}`}
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              name="email-template-body"
              label={t('settings.email.template_body')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={!canEdit}
              rows={10}
              maxLength={TEMPLATE_BODY_MAX}
              hint={t('settings.email.template_body_hint')}
            />
            <div>
              <p className={cn(TYPE.label, 'mb-s2 text-ds-text')}>
                {t('settings.email.preview_title')}
              </p>
              <p className={cn(TYPE.body, 'mb-s2 text-ds-text')}>
                <span className="text-ds-muted">{t('settings.email.template_subject')}: </span>
                {samplePreview.subject}
              </p>
              <iframe
                title={t('settings.email.preview_title')}
                className="h-[420px] w-full rounded-xl border border-ds-line bg-ds-sunk"
                sandbox=""
                srcDoc={samplePreview.html}
              />
            </div>
            {canEdit ? (
              <div className="flex flex-col-reverse gap-s2 nav:flex-row nav:justify-end">
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={() => void onResetTemplate()}
                  disabled={resetTemplate.isPending || !override}
                >
                  {t('settings.email.reset_default')}
                </Button>
                <Button type="submit" loading={upsertTemplate.isPending}>
                  {t('settings.email.save_template')}
                </Button>
              </div>
            ) : null}
          </div>
        </SectionCard>
      </form>
    </div>
  );
}
